import { useState } from 'react';
import { useAuth, useHasPermission, useFeature } from '@/hooks/useAuth';
import { useOpportunities } from '@/hooks/useOpportunities';
import { useProjects } from '@/hooks/useProjects';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { usePipelineForecast } from '@/hooks/useForecast';
import {
  TrendingUp,
  FileText,
  Users,
  ArrowRight,
  Clock,
  Target,
  Rocket,
  AlertTriangle,
  Calendar,
  CheckSquare,
  Inbox,
  Award,
  DollarSign,
  CheckCircle2,
  Activity,
  Briefcase,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';

type PersonaTab = 'overview' | 'sales' | 'presales' | 'delivery';

// Format today's date
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canViewPipeline = useHasPermission('pipeline.summary.view');
  const canViewProjects = useHasPermission('projects.view');
  const showPersonas = useFeature('persona_dashboards');
  const showForecast = useFeature('pipeline_forecast');
  const [activeTab, setActiveTab] = useState<PersonaTab>('overview');

  const { data: opportunitiesData, isLoading: oppsLoading } = useOpportunities({
    status: 'pipeline',
    limit: 5,
  });
  const { data: projectsData, isLoading: projectsLoading } = useProjects({ limit: 5 });
  const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics();
  const { data: forecast } = usePipelineForecast();

  const isLoading = oppsLoading || projectsLoading || metricsLoading;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Welcome header */}
      <div>
        <h1 className="font-heading text-xl md:text-2xl font-bold text-xtravirt-dark">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {formatDate(new Date())}
        </p>
      </div>

      {/* Persona tabs (gated by feature flag) */}
      {showPersonas && (
        <>
          <div className="border-b border-gray-200 -mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto">
            <nav className="flex gap-4 md:gap-6 min-w-max">
              <PersonaTabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} label="Overview" />
              <PersonaTabButton active={activeTab === 'sales'} onClick={() => setActiveTab('sales')} icon={Target} label="Sales" />
              <PersonaTabButton active={activeTab === 'presales'} onClick={() => setActiveTab('presales')} icon={CheckSquare} label="Pre-Sales" />
              <PersonaTabButton active={activeTab === 'delivery'} onClick={() => setActiveTab('delivery')} icon={Rocket} label="Delivery" />
            </nav>
          </div>
          {activeTab === 'sales' && metrics && <SalesDashboard metrics={metrics.sales} />}
          {activeTab === 'presales' && metrics && <PreSalesDashboard metrics={metrics.preSales} />}
          {activeTab === 'delivery' && metrics && <DeliveryDashboard metrics={metrics.delivery} />}
        </>
      )}

      {(!showPersonas || activeTab === 'overview') && metrics && (
      <>
      {/* For You — personalized action queue */}
      <ForYouPanel
        forYou={metrics.forYou}
        canViewPipeline={canViewPipeline}
        canViewProjects={canViewProjects}
      />

      {/* Business at a glance — KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {canViewPipeline && (
          <MetricCard
            icon={TrendingUp}
            label="Pipeline"
            value={formatCurrency(metrics.overview.pipelineValue)}
            subLabel={`${metrics.overview.pipelineCount} ${metrics.overview.pipelineCount === 1 ? 'opp' : 'opps'}`}
            color="cyan"
          />
        )}
        {canViewPipeline && (
          <MetricCard
            icon={DollarSign}
            label="Committed"
            value={formatCurrency(metrics.overview.committedValue)}
            subLabel={`${metrics.overview.committedCount} ${metrics.overview.committedCount === 1 ? 'deal' : 'deals'}`}
            color="green"
          />
        )}
        {canViewProjects && (
          <DeliveryKpiCard
            count={metrics.overview.inDeliveryCount}
            health={metrics.overview.inDeliveryHealth}
          />
        )}
        {canViewPipeline && showForecast && forecast && (
          <MetricCard
            icon={Activity}
            label="Forecast (weighted)"
            value={formatCurrency(forecast.summary.totalForecast)}
            subLabel="committed + weighted pipeline"
            color="amber"
          />
        )}
        {canViewPipeline && (!showForecast || !forecast) && (
          <MetricCard
            icon={Briefcase}
            label="Avg deal size"
            value={
              metrics.overview.pipelineCount > 0
                ? formatCurrency(metrics.overview.pipelineValue / metrics.overview.pipelineCount)
                : formatCurrency(0)
            }
            subLabel="pipeline only"
            color="dark"
          />
        )}
      </div>

      {/* Recent Activity — stacked on mobile, side by side on desktop */}
      <div className="grid gap-4 md:gap-6 grid-cols-1 lg:grid-cols-2">
        {canViewPipeline && (
          <div className="card">
            <div className="section-header">
              <h2 className="font-heading text-lg font-semibold text-xtravirt-dark">Recent Opportunities</h2>
              <Link to="/opportunities" className="text-sm font-medium text-xtravirt-cyan hover:underline flex items-center gap-1">
                View all <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-4">
              {opportunitiesData?.opportunities.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No opportunities yet</p>
              ) : (
                <div className="project-list">
                  {opportunitiesData?.opportunities.map((opp) => (
                    <div
                      key={opp.id}
                      onClick={() => navigate(`/opportunities/${opp.id}`)}
                      className="project-list-item"
                    >
                      <div className="project-list-icon bg-blue-50">
                        <TrendingUp className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="project-list-content">
                        <span className="project-list-title">{opp.title}</span>
                        <span className="project-list-meta">{opp.customerName}</span>
                      </div>
                      <span className="text-xs md:text-sm text-gray-500 flex-shrink-0">
                        {opp.projectCount || 0} project{(opp.projectCount || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {canViewProjects && (
          <div className="card">
            <div className="section-header">
              <h2 className="font-heading text-lg font-semibold text-xtravirt-dark">Recent Projects</h2>
              <Link to="/projects" className="text-sm font-medium text-xtravirt-cyan hover:underline flex items-center gap-1">
                View all <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-4">
              {projectsData?.projects.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No projects yet</p>
              ) : (
                <div className="project-list">
                  {projectsData?.projects.map((project) => (
                    <div
                      key={project.id}
                      onClick={() => navigate(`/projects/${project.id}`)}
                      className="project-list-item"
                    >
                      <div className="project-list-icon bg-purple-50">
                        <FileText className="h-4 w-4 text-purple-600" />
                      </div>
                      <div className="project-list-content">
                        <span className="project-list-title">{project.name}</span>
                        <span className="project-list-meta">{project.customerName}</span>
                      </div>
                      <span className="text-xs md:text-sm text-green-700 font-medium flex-shrink-0">
                        {(project.totalAdjustedDays / 100).toFixed(1)} days
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

// ============ For You Panel ============

function ForYouPanel({
  forYou,
  canViewPipeline,
  canViewProjects,
}: {
  forYou: import('@/hooks/useDashboardMetrics').DashboardMetrics['forYou'];
  canViewPipeline: boolean;
  canViewProjects: boolean;
}) {
  const reviewsCount = forYou.reviewsWaiting;
  const attentionCount = forYou.projectsNeedingAttention.length;
  const staleCount = forYou.staleOpportunities.length;
  const totalActions = reviewsCount + attentionCount + staleCount;

  if (totalActions === 0) {
    return (
      <div className="card p-4 md:p-5">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-50 flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h2 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">You're all caught up</h2>
            <p className="text-xs md:text-sm text-gray-500">No reviews, at-risk projects, or stale opportunities right now.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4 md:p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">For you</h2>
        <span className="text-xs text-gray-500">{totalActions} item{totalActions === 1 ? '' : 's'} need attention</span>
      </div>
      <div className="space-y-3">
        {/* Reviews waiting */}
        {canViewProjects && reviewsCount > 0 && (
          <Link
            to="/reviews"
            className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors"
          >
            <div className="flex-shrink-0 h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center">
              <Inbox className="h-4 w-4 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-900">
                {reviewsCount} review{reviewsCount === 1 ? '' : 's'} waiting
              </p>
              <p className="text-xs text-amber-700">Submitted projects you can claim or are reviewing</p>
            </div>
            <ArrowRight className="h-4 w-4 text-amber-700 flex-shrink-0" />
          </Link>
        )}

        {/* Projects needing attention */}
        {canViewProjects && attentionCount > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50">
            <div className="flex items-center gap-3 p-3 border-b border-red-100">
              <div className="flex-shrink-0 h-9 w-9 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-red-700" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">
                  {attentionCount} project{attentionCount === 1 ? '' : 's'} need attention
                </p>
                <p className="text-xs text-red-700">Health is amber or red on projects you own or lead</p>
              </div>
            </div>
            <div className="divide-y divide-red-100">
              {forYou.projectsNeedingAttention.map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-red-100 transition-colors"
                >
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full flex-shrink-0',
                      p.healthStatus === 'red' ? 'bg-red-500' : 'bg-amber-500'
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-xtravirt-dark truncate">{p.name}</p>
                    <p className="text-xs text-gray-600 truncate">
                      {p.customerName}
                      {p.currentScopingStage && ` · ${p.currentScopingStage.replace(/_/g, ' ')}`}
                    </p>
                  </div>
                  {p.totalCost > 0 && (
                    <span className="text-xs text-gray-600 flex-shrink-0">{formatCurrency(p.totalCost)}</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Stale opportunities */}
        {canViewPipeline && staleCount > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3 p-3 border-b border-gray-200">
              <div className="flex-shrink-0 h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center">
                <Clock className="h-4 w-4 text-gray-700" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {staleCount} opportunit{staleCount === 1 ? 'y' : 'ies'} going stale
                </p>
                <p className="text-xs text-gray-600">Pipeline opps you own with no update in 14+ days</p>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {forYou.staleOpportunities.map((o) => (
                <Link
                  key={o.id}
                  to={`/opportunities/${o.id}`}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-xtravirt-dark truncate">{o.title}</p>
                    <p className="text-xs text-gray-600 truncate">{o.customerName}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-gray-500">{o.daysSinceUpdate}d ago</span>
                    {o.servicesValue > 0 && (
                      <span className="text-xs font-medium text-gray-700">{formatCurrency(o.servicesValue)}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Delivery KPI card with inline RAG dots ============

function DeliveryKpiCard({
  count,
  health,
}: {
  count: number;
  health: { green: number; amber: number; red: number; na: number };
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <Rocket className="h-8 w-8 flex-shrink-0 text-xtravirt-dark" />
        <div className="min-w-0">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">In Delivery</p>
          <p className="text-lg md:text-xl font-heading font-bold text-xtravirt-dark mt-0.5">
            {count} {count === 1 ? 'project' : 'projects'}
          </p>
          {count > 0 ? (
            <div className="flex items-center gap-2 mt-1 text-xs">
              {health.green > 0 && (
                <span className="flex items-center gap-1 text-green-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  {health.green}
                </span>
              )}
              {health.amber > 0 && (
                <span className="flex items-center gap-1 text-amber-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  {health.amber}
                </span>
              )}
              {health.red > 0 && (
                <span className="flex items-center gap-1 text-red-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                  {health.red}
                </span>
              )}
              {health.na > 0 && (
                <span className="flex items-center gap-1 text-gray-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                  {health.na}
                </span>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 mt-1">none active</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Persona Dashboard Components ============

function PersonaTabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 md:gap-2 border-b-2 py-3 text-xs md:text-sm font-medium transition-colors whitespace-nowrap',
        active
          ? 'border-xtravirt-cyan text-xtravirt-cyan'
          : 'border-transparent text-gray-600 hover:text-xtravirt-dark'
      )}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {label}
    </button>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  subLabel,
  color = 'cyan',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  subLabel?: string;
  color?: 'green' | 'cyan' | 'amber' | 'red' | 'dark';
}) {
  const colors = {
    green: 'text-green-600',
    cyan: 'text-xtravirt-cyan',
    amber: 'text-amber-600',
    red: 'text-red-600',
    dark: 'text-xtravirt-dark',
  };
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <Icon className={cn('h-8 w-8 flex-shrink-0', colors[color])} />
        <div className="min-w-0">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</p>
          <p className="text-lg md:text-xl font-heading font-bold text-xtravirt-dark mt-0.5 truncate">{value}</p>
          {subLabel && <p className="text-xs text-gray-400 mt-0.5 truncate">{subLabel}</p>}
        </div>
      </div>
    </div>
  );
}

// ============ Sales Dashboard ============

function SalesDashboard({ metrics }: { metrics: import('@/hooks/useDashboardMetrics').DashboardMetrics['sales'] }) {
  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <MetricCard icon={TrendingUp} label="Pipeline Value" value={formatCurrency(metrics.pipelineValue)} color="cyan" />
        <MetricCard icon={DollarSign} label="Committed" value={formatCurrency(metrics.committedValue)} color="green" />
        <MetricCard icon={Award} label="Win Rate (90d)" value={`${metrics.winRate90d}%`} subLabel={`${metrics.winsCount90d} won / ${metrics.lossesCount90d} lost`} color="amber" />
        <MetricCard icon={Clock} label="Avg Sales Cycle" value={`${metrics.avgCycleDays}d`} subLabel="last 90 days" color="dark" />
      </div>

      {/* Pipeline by stage funnel */}
      <div className="card p-4 md:p-6">
        <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-3">Pipeline by Stage</h3>
        <div className="space-y-2">
          {Object.entries(metrics.byStage).map(([stage, data]) => {
            const maxValue = Math.max(...Object.values(metrics.byStage).map((s) => s.value), 1);
            const pct = (data.value / maxValue) * 100;
            return (
              <div key={stage}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="capitalize text-gray-700">{stage.replace(/_/g, ' ')}</span>
                  <span className="text-gray-500">
                    {data.count} · {formatCurrency(data.value)}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-xtravirt-cyan rounded-full" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* At Risk */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">At Risk</h3>
          </div>
          {metrics.atRisk.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No at-risk opportunities</p>
          ) : (
            <div className="space-y-2">
              {metrics.atRisk.map((opp) => (
                <Link
                  key={opp.id}
                  to={`/opportunities/${opp.id}`}
                  className="flex items-center justify-between gap-2 p-2 rounded hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-xtravirt-dark truncate">{opp.title}</p>
                    <p className="text-xs text-gray-500 truncate">{opp.customerName}</p>
                  </div>
                  <span className="inline-block h-2 w-2 rounded-full bg-red-500 flex-shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Won This Month */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-green-600" />
              <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">Won This Month</h3>
            </div>
            {metrics.wonThisMonthValue > 0 && (
              <span className="text-sm font-semibold text-green-700">
                {formatCurrency(metrics.wonThisMonthValue)}
              </span>
            )}
          </div>
          {metrics.wonThisMonth.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No wins this month yet</p>
          ) : (
            <div className="space-y-2">
              {metrics.wonThisMonth.map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  className="flex items-center justify-between gap-2 p-2 rounded hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-xtravirt-dark truncate">{p.name}</p>
                    <p className="text-xs text-gray-500 truncate">{p.customerName}</p>
                  </div>
                  <span className="text-sm font-semibold text-green-700 flex-shrink-0">
                    {formatCurrency(p.totalCost)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Pre-Sales Dashboard ============

function PreSalesDashboard({ metrics }: { metrics: import('@/hooks/useDashboardMetrics').DashboardMetrics['preSales'] }) {
  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        <MetricCard icon={Target} label="Active Scopings" value={metrics.activeScopings.toString()} color="cyan" />
        <MetricCard icon={Inbox} label="Pending Reviews" value={metrics.pendingReviews.toString()} color="amber" />
        <MetricCard icon={Calendar} label="Workshops This Week" value={metrics.workshopsScheduled.length.toString()} color="dark" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* My Active Scopings */}
        <div className="card p-4 md:p-6">
          <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-3">
            My Active Scopings
          </h3>
          {metrics.myActiveScopings.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No active scopings assigned to you</p>
          ) : (
            <div className="space-y-2">
              {metrics.myActiveScopings.map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  className="flex items-center justify-between gap-2 p-2 rounded hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-xtravirt-dark truncate">{p.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {p.customerName} · {p.currentScopingStage?.replace(/_/g, ' ')}
                    </p>
                  </div>
                  {p.totalCost > 0 && (
                    <span className="text-xs text-green-700 font-medium flex-shrink-0">
                      {formatCurrency(p.totalCost)}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Workshops Scheduled */}
        <div className="card p-4 md:p-6">
          <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-3">
            Upcoming Workshops
          </h3>
          {metrics.workshopsScheduled.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No workshops scheduled this week</p>
          ) : (
            <div className="space-y-2">
              {metrics.workshopsScheduled.map((w) => (
                <Link
                  key={w.id}
                  to={`/projects/${w.id}`}
                  className="flex items-center justify-between gap-2 p-2 rounded hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-xtravirt-dark truncate">{w.name}</p>
                    <p className="text-xs text-gray-500 truncate">{w.customerName}</p>
                  </div>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {new Date(w.targetDate).toLocaleDateString()}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Delivery Dashboard ============

function DeliveryDashboard({ metrics }: { metrics: import('@/hooks/useDashboardMetrics').DashboardMetrics['delivery'] }) {
  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <MetricCard icon={Rocket} label="Active Projects" value={metrics.activeProjects.toString()} color="cyan" />
        <MetricCard icon={Users} label="Customers" value={metrics.uniqueCustomers.toString()} color="dark" />
        <MetricCard icon={DollarSign} label="Total Value" value={formatCurrency(metrics.totalValue)} color="green" />
        <MetricCard icon={Calendar} label="Days This Month" value={metrics.daysThisMonth.toFixed(0)} color="amber" />
      </div>

      {/* Health breakdown */}
      <div className="card p-4 md:p-6">
        <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-3">
          Delivery Health
        </h3>
        <div className="grid grid-cols-4 gap-3">
          <HealthCell label="On Track" count={metrics.health.green} total={metrics.activeProjects} color="green" />
          <HealthCell label="Attention" count={metrics.health.amber} total={metrics.activeProjects} color="amber" />
          <HealthCell label="Critical" count={metrics.health.red} total={metrics.activeProjects} color="red" />
          <HealthCell label="Not Set" count={metrics.health.na} total={metrics.activeProjects} color="gray" />
        </div>
      </div>

      <div className="card p-4 md:p-6">
        <p className="text-sm text-gray-500">
          For project details, see the <Link to="/delivery" className="text-xtravirt-cyan hover:underline">Delivery page</Link>.
        </p>
      </div>
    </div>
  );
}

function HealthCell({ label, count, total, color }: { label: string; count: number; total: number; color: 'green' | 'amber' | 'red' | 'gray' }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const colorMap = {
    green: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    red: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
    gray: { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' },
  };
  const cfg = colorMap[color];
  return (
    <div className={cn('rounded-lg p-3 text-center', cfg.bg)}>
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <span className={cn('h-2 w-2 rounded-full', cfg.dot)} />
        <span className={cn('text-xs font-medium', cfg.text)}>{label}</span>
      </div>
      <p className={cn('text-2xl font-heading font-bold', cfg.text)}>{count}</p>
      <p className="text-xs text-gray-500 mt-0.5">{pct}%</p>
    </div>
  );
}

function formatCurrency(cents: number): string {
  if (cents >= 100000000) {
    return `$${(cents / 100000000).toFixed(1)}M`;
  }
  if (cents >= 100000) {
    return `$${(cents / 100000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
